/**
 * heatHaze — rippling hot-air shimmer near the ground (volcanic / desert /
 * scorched scenes). Each patch is a large, very-low-alpha additive sprite
 * that WOBBLES horizontally and PULSES its scale on a medium-frequency
 * cycle, evoking the lensing distortion of heated air rising. Distinct from
 * `caustics` (water-light ripples — sharp glow_tight sprites, wobble only,
 * no scale pulse) and `flicker` (fast torch shimmer — small anchored points,
 * high-frequency alpha). A drifting ambient bed (Z_LAYERS.ATMOSPHERE,
 * mooded:true).
 *
 * Read rule: heat haze is SUBTLE by nature — low alpha (0.04–0.14) over the
 * ground band. The scale pulse (0.8→1.2) is what reads as "shimmer" — the
 * patch grows and shrinks like a lensing bubble. Keep colours warm
 * (ember/amber/sand) and the band low (bandYMin 0.75, bandYMax 0.98).
 *
 * Same builder shape as the kit: narrow by kind, place from the seed, mint
 * soft sprites behind IRenderer (null-safe), drive them with ONE shared
 * ctx.addTick, return a sprite handle, self-register. REDUCE-MOTION: the
 * wobble + scale pulse is the builder's own tick → when reduced we place the
 * patches STATIC at their mid-alpha + base scale and never tick. The pref is
 * read ONCE at build.
 *
 * NOTE: A true DisplacementFilter (Pixi) would give pixel-accurate heat
 * distortion, but the rendering seam (WorldLayerHandle) is intentionally
 * opaque — only addChild/setAlpha/destroy, no filter access. This sprite-
 * based shimmer is the cleanest path within the existing architecture.
 */
import { registerEffect, makeContentHandle, type EffectBuilder } from '../effectBuilders';
import { seededRand } from '../atmospherePlacement';
import { createGlowSprite } from '../../vfx/glowSpriteFactory';
import { vfxBudgetManager } from '../../VFXBudgetManager';
import { getMotionPrefs } from '../../../systems/accessibility/motionPrefs';
import { Z_LAYERS } from '../../../rendering/Z_LAYERS';
import { lerp } from '../../../../shared/utils/MathUtils';
import type { ISprite } from '../../../rendering/IRenderer';

const TWO_PI = Math.PI * 2;

const buildHeatHaze: EffectBuilder = (layer, ctx) => {
  if (layer.kind !== 'heatHaze') return null;
  const { region } = ctx;
  const spanY = region.y1 - region.y0;
  const reduceMotion = getMotionPrefs().reduceMotion;

  const sprites: ISprite[] = [];
  // Per-patch shimmer state: sprite, base x/y, async period + phase + scale phase.
  const patches: { s: ISprite; baseX: number; baseY: number; periodMs: number; phase: number; scalePhase: number }[] = [];

  for (let i = 0; i < layer.count; i += 1) {
    if (!vfxBudgetManager.canSpawnShape()) break;
    const seed = ctx.seed + 6600 + i * 29.3;
    const size = lerp(layer.sizeMin, layer.sizeMax, seededRand(seed));
    const x = lerp(region.x0, region.x1, seededRand(seed + 1));
    const y = region.y0 + spanY * lerp(layer.bandYMin, layer.bandYMax, seededRand(seed + 2));
    const alpha = lerp(layer.alphaMin, layer.alphaMax, 0.5);
    // glow_soft = the diffuse heat blob (hot air reads as a soft wash, not a sharp ripple).
    const s = createGlowSprite({ x, y, size, color: layer.color, alpha, textureKey: 'glow_soft', blendMode: 'add', parent: ctx.layerContainer });
    if (!s) break; // textures not ready — quieter scene, never a crash
    sprites.push(s);
    if (!reduceMotion) {
      patches.push({
        s, baseX: x, baseY: y,
        periodMs: lerp(layer.periodMinMs, layer.periodMaxMs, seededRand(seed + 3)),
        phase: seededRand(seed + 4),
        scalePhase: seededRand(seed + 5),
      });
    }
  }

  if (!sprites.length) return null;
  if (reduceMotion || !patches.length) return makeContentHandle({ sprites });

  let elapsedMs = 0;
  const unsub = ctx.addTick((dtMs: number) => {
    elapsedMs += dtMs;
    for (const p of patches) {
      const ph = (elapsedMs / p.periodMs + p.phase) % 1;
      const scPh = (elapsedMs / p.periodMs + p.scalePhase) % 1;
      // Alpha shimmer: sine sweep min→max→min.
      const a = lerp(layer.alphaMin, layer.alphaMax, 0.5 - 0.5 * Math.cos(ph * TWO_PI));
      // Horizontal wobble — the ripple of heated air drifting sideways.
      const wobbleX = Math.sin(ph * TWO_PI) * layer.driftPx;
      // Scale pulse — the lensing bubble grows and shrinks (the visual key
      // that reads as "heat" rather than just "fog wobble"). 0.8→1.2 range.
      const scale = 1 + Math.sin(scPh * TWO_PI) * 0.2;
      p.s.setAlpha(a);
      p.s.setPosition(p.baseX + wobbleX, p.baseY);
      p.s.setScale?.(scale);
    }
  });

  return makeContentHandle({ sprites, onDestroy: unsub });
};

registerEffect({ kind: 'heatHaze', build: buildHeatHaze, depth: Z_LAYERS.ATMOSPHERE });
